import { navigate } from 'gatsby';
import { useTranslation } from 'react-i18next';
import { type ChangeEvent, type MouseEvent, useEffect, useState } from 'react';
import { Avatar, Button, Flag, Help, Icon, NpsScore, UserMenu, useToastNotify } from '@opswat/ui';
import { NotificationCenter } from 'components/NotificationCenter';
import { useTheme } from 'hooks/useTheme';
import { useLanguage } from 'hooks/useLanguage';
import { useQueryFilter } from 'hooks/useQueryFilter';
import { dayjs } from 'services/dayjs';
import { SettingsTabs } from 'services/tabs';
import { signOut } from 'services/authenticator';
import { npsStorageKeys, useSendNpsScore } from 'services/api/Nps';
import { useUser } from 'services/User';

type VisibleMenu = 'default' | 'organization' | 'theme' | 'language';

interface BackMenuItemProps {
    onClick: (event: MouseEvent) => void;
    label: string;
}

const getLabelText = (score: number | null, productName: string, t: (key: string, options?: { productName?: string; }) => string) => {
    if (score === null) {
        return t('What would make {{productName}} work better for your needs?', { productName });
    }

    if (score <= 4) {
        return t('We\'re sorry to hear that. What could we do better?');
    }

    if (score <= 7) {
        return t('Thanks! What would make your experience even better?');
    }

    return t('Thanks! What would take your experience from good to great?');
};

const BackMenuItem = ({ onClick, label }: BackMenuItemProps) => (
    <UserMenu.Item onClick={onClick} className='tw:text-neutrals-800 tw:dark:text-neutrals-500'>
        <Icon iconName='input-left' className='tw:bg-neutrals-800 tw:dark:bg-neutrals-500' />
        {label}
    </UserMenu.Item>
);

export const PageHeader = () => {
    const { t } = useTranslation();
    const { user } = useUser();
    const { organizationIds, setGlobal, clearGlobal } = useQueryFilter();
    const { theme, themeOptions, currentTheme, setTheme } = useTheme();
    const { languages, currentLanguage, changeLanguage } = useLanguage();
    const fullName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`;
    const organizationId = user?.organization?.organization_id ?? '';
    const [visibleMenu, setVisibleMenu] = useState<VisibleMenu>('default');
    const [isNpsOpen, setIsNpsOpen] = useState(false);
    const [npsStep, setNpsStep] = useState<'score' | 'thank-you'>('score');
    const [npsScore, setNpsScore] = useState<number | null>(null);
    const [npsFeedback, setNpsFeedback] = useState('');
    const [canContactNps, setCanContactNps] = useState(false);
    const { toastNotify } = useToastNotify();
    const { trigger: sendNps } = useSendNpsScore();
    const [pendingNpsValues, setPendingNpsValues] = useState<{ score: number | null; feedback: string; canContact: boolean; } | null>(null);

    const handleNpsSkip = () => {
        if (typeof window === 'undefined') {
            return;
        }
        localStorage.setItem(npsStorageKeys.visits, '0');
        setPendingNpsValues(null);
        setNpsStep('score');
        setNpsScore(null);
        setNpsFeedback('');
        setCanContactNps(false);
        setIsNpsOpen(false);
    };

    const handleNpsSkip30Days = () => {
        if (typeof window === 'undefined') {
            return;
        }
        localStorage.setItem(npsStorageKeys.snoozeUntil, String(dayjs().add(30, 'days').valueOf()));
        localStorage.setItem(npsStorageKeys.visits, '0');
        setPendingNpsValues(null);
        setNpsStep('score');
        setNpsScore(null);
        setNpsFeedback('');
        setCanContactNps(false);
        setIsNpsOpen(false);
    };

    const handleNpsSubmit = (payload: { score: number | null; feedback: string; canContact: boolean; }) => {
        setPendingNpsValues(payload);
    };

    const handleNpsFinish = async () => {
        if (typeof window === 'undefined') {
            return;
        }
        if (!pendingNpsValues) {
            // If Finish is clicked without a payload (unexpected), treat as close
            handleNpsSkip();
            return;
        }
        try {
            await sendNps(pendingNpsValues);
            localStorage.setItem(npsStorageKeys.submitted, '1');
            localStorage.removeItem(npsStorageKeys.snoozeUntil);
            localStorage.setItem(npsStorageKeys.visits, '0');
            setPendingNpsValues(null);
            setNpsStep('score');
            setNpsScore(null);
            setNpsFeedback('');
            setCanContactNps(false);
            setIsNpsOpen(false);
        } catch (error) {

            toastNotify({
                title: t('Error'),
                // @ts-expect-error backend error shape
                message: error?.response?.data?.error?.messages?.[0],
                type: 'error',
                hasClose: true,
                duration: 4000,
            });
        }
    };

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const handler = () => setIsNpsOpen(true);
        window.addEventListener('md_nps_open', handler);
        return () => window.removeEventListener('md_nps_open', handler);
    }, []);

    const handlePersonalScope = () => {
        clearGlobal('organizationIds', 'cursor', 'offset');
    };

    const handleOrganizationScope = () => {
        setGlobal(prev => {
            if ((!prev?.organizationIds || prev?.organizationIds?.length === 0) && organizationId) {
                return { ...prev, organizationIds: [organizationId] };
            }

            return prev;
        });
    };

    const handleMenuChange = (menu: VisibleMenu) => (event: MouseEvent) => {
        event.preventDefault();
        setVisibleMenu(menu);
    };

    const handleLogout = () => {
        clearGlobal();
        signOut();
    };

    return (
        <div className='tw:inline-flex tw:items-center tw:gap-20'>
            <div className='tw:flex tw:flex-row tw:gap-8'>
                <NpsScore
                    open={isNpsOpen}
                    onOpenChange={(open) => {
                        if (!open && isNpsOpen) {
                            handleNpsSkip();
                            return;
                        }
                        if (!open) setPendingNpsValues(null);
                        setIsNpsOpen(open);
                    }}
                    contentClassName='tw:w-520 tw:max-w-520'
                >
                    {npsStep === 'score' ? (
                        <>
                            <NpsScore.Title className='tw:text-xl'>{t('Tell us what you think')}</NpsScore.Title>
                            <NpsScore.Body className='tw:flex tw:flex-col tw:gap-40 tw:mt-20'>
                                <div className='tw:flex tw:flex-col tw:gap-20'>
                                    <NpsScore.Description className='tw:text-body tw:text-neutrals-1200 tw:dark:text-white'>
                                        {t('Based on your experience, how likely are you to recommend ')}
                                        {t('MetaDefender Cloud')}
                                        {t(' to a colleague?')}
                                    </NpsScore.Description>
                                    <div className='tw:flex tw:flex-col tw:gap-8'>
                                        <NpsScore.Score
                                            ariaLabel={t('Net promoter score')}
                                            value={npsScore}
                                            onValueChange={setNpsScore}
                                        />
                                        <div className='tw:flex tw:text-xs tw:items-center tw:justify-between tw:text-caption tw:text-neutrals-1200 tw:dark:text-white'>
                                            <div>{t('Not likely at all')}</div>
                                            <div>{t('Extremely likely')}</div>
                                        </div>
                                    </div>
                                </div>
                                <div className='tw:flex tw:flex-col tw:gap-20'>
                                    <NpsScore.Textarea
                                        id='nps-score-feedback'
                                        label={(
                                            <>
                                                {getLabelText(npsScore, t('MetaDefender Cloud'), t)}
                                                {npsScore !== null && npsScore <= 7 ? (
                                                    <span className='tw:text-red-900 tw:dark:text-red-300'>*</span>
                                                ) : null}
                                            </>
                                        )}
                                        aria-required={npsScore !== null && npsScore <= 7}
                                        placeholder={t('Type your feedback here')}
                                        value={npsFeedback}
                                        onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                                            setNpsFeedback(event.target.value)}
                                        rows={3}
                                    />
                                    <NpsScore.Contact
                                        id='nps-score-contact'
                                        checked={canContactNps}
                                        onCheckedChange={(checked: boolean | 'indeterminate') =>
                                            setCanContactNps(checked === true)}
                                    >
                                        {t('I agree to be contacted by OPSWAT regarding my experience')}
                                    </NpsScore.Contact>
                                </div>
                            </NpsScore.Body>
                            <NpsScore.Footer className='tw:flex tw:items-center tw:justify-between tw:gap-20'>
                                <Button variant='tertiary' text={t('Skip for 30 days')} onClick={handleNpsSkip30Days} />
                                <div className='tw:flex tw:items-center tw:gap-8'>
                                    <Button variant='secondary' text={t('Skip')} onClick={handleNpsSkip} />
                                    <Button
                                        variant='primary'
                                        text={t('Submit')}
                                        onClick={() => {
                                            const payload = {
                                                score: npsScore,
                                                feedback: npsFeedback,
                                                canContact: canContactNps,
                                            };
                                            handleNpsSubmit(payload);
                                            setNpsStep('thank-you');
                                        }}
                                        disabled={npsScore === null || (npsScore <= 7 && npsFeedback.trim().length === 0)}
                                    />
                                </div>
                            </NpsScore.Footer>
                        </>
                    ) : (
                        <NpsScore.ThankYou>
                            <NpsScore.Title className='tw:text-headline-m'>{t('Thank You')}</NpsScore.Title>
                            <NpsScore.Body className='tw:flex tw:flex-col tw:gap-8'>
                                <NpsScore.Description className='tw:text-body tw:text-neutrals-1200 tw:dark:text-white tw:whitespace-pre-line'>
                                    {t('We appreciate your input.\nWe\'re listening and working to improve your experience.')}
                                </NpsScore.Description>
                            </NpsScore.Body>
                            <NpsScore.Footer className='tw:flex tw:items-center tw:justify-end tw:gap-8'>
                                <Button
                                    variant='secondary'
                                    text={t('Share Another Response')}
                                    onClick={() => {
                                        setPendingNpsValues(null);
                                        setNpsScore(null);
                                        setNpsFeedback('');
                                        setCanContactNps(false);
                                        setNpsStep('score');
                                    }}
                                />
                                <Button variant='primary' text={t('Finish')} onClick={() => void handleNpsFinish()} />
                            </NpsScore.Footer>
                        </NpsScore.ThankYou>
                    )}
                </NpsScore>
                <Help>
                    <Help.Trigger>
                        <div className='tw:flex tw:items-center tw:rounded-sm hover:tw:bg-neutrals-200 hover:tw:dark:bg-neutrals-1100'>
                            <Icon iconName='help-1' />
                            <span className='tw:hidden sm:tw:inline'>{t('Help')}</span>
                        </div>
                    </Help.Trigger>
                    <Help.Dropdown>
                        <Help.Item onClick={() => window.open('https://www.opswat.com/docs/mdcloud', '_blank')}>
                            {t('Documentation')}
                        </Help.Item>
                        <Help.Item onClick={() => window.open('https://www.opswat.com/legal/privacy-policy', '_blank')}>
                            {t('Privacy Policy')}
                        </Help.Item>
                        <Help.Item onClick={() => window.open('https://www.opswat.com/legal/terms-of-service', '_blank')}>
                            {t('Terms of Service')}
                        </Help.Item>
                        <Help.Item onClick={() => setIsNpsOpen(true)}>
                            {t('Show NPS Score')}
                        </Help.Item>
                    </Help.Dropdown>
                </Help>
                <NotificationCenter />
            </div>
            <UserMenu onOpenChange={() => setVisibleMenu('default')}>
                <UserMenu.Trigger onClick={() => setVisibleMenu('default')}>
                    <Avatar name={fullName} />
                </UserMenu.Trigger>
                <UserMenu.Dropdown className='tw:w-236'>
                    {visibleMenu === 'default' ? (
                        <>
                            <UserMenu.Item>
                                <div className='tw:flex tw:flex-col'>
                                    <div className='tw:text-headline'>{fullName}</div>
                                    <div className='tw:text-body tw:text-neutrals-700 tw:dark:text-neutrals-500 tw:break-all'>{user?.email ?? ''}</div>
                                </div>
                            </UserMenu.Item>
                            <UserMenu.Divider />
                            <UserMenu.Item onClick={() => navigate(`/settings/${SettingsTabs.Profile}`)}>
                                <div className='tw:flex tw:items-center tw:gap-8'>
                                    <Icon iconName='user' />
                                    <span>{t('Profile')}</span>
                                </div>
                            </UserMenu.Item>
                            <UserMenu.Divider />
                            <UserMenu.Item onClick={handleMenuChange('organization')}>
                                <div className='tw:flex tw:items-center tw:gap-8 tw:justify-between tw:w-full'>
                                    <div className='tw:flex tw:items-center tw:gap-8'>
                                        <Icon iconName='organization' />
                                        <span>{t('Organization')}</span>
                                    </div>
                                    <Icon iconName='input-right' />
                                </div>
                            </UserMenu.Item>
                            <UserMenu.Item onClick={handleMenuChange('language')}>
                                <div className='tw:flex tw:items-center tw:gap-8 tw:justify-between tw:w-full'>
                                    <div className='tw:flex tw:items-center tw:gap-8'>
                                        <Flag countryCode={currentLanguage?.flag ?? 'US'} />
                                        <span>{currentLanguage?.name ?? t('English')}</span>
                                    </div>
                                    <Icon iconName='input-right' />
                                </div>
                            </UserMenu.Item>
                            <UserMenu.Item onClick={handleMenuChange('theme')}>
                                <div className='tw:flex tw:items-center tw:gap-8 tw:justify-between tw:w-full'>
                                    <div className='tw:flex tw:items-center tw:gap-8'>
                                        <Icon iconName={currentTheme.icon} />
                                        <span>{currentTheme.label}</span>
                                    </div>
                                    <Icon iconName='input-right' />
                                </div>
                            </UserMenu.Item>
                            <UserMenu.Divider />
                            <UserMenu.Item onClick={handleLogout}>
                                <div className='tw:flex tw:items-center tw:gap-8'>
                                    <Icon iconName='logout' />
                                    <span>{t('Logout')}</span>
                                </div>
                            </UserMenu.Item>
                        </>
                    ) : null}

                    {visibleMenu === 'organization' ? (
                        <>
                            <BackMenuItem onClick={handleMenuChange('default')} label={t('Back')} />
                            <UserMenu.Divider />
                            <UserMenu.Item onClick={handlePersonalScope}>
                                <div className='tw:flex tw:items-center tw:gap-8'>
                                    <Icon iconName='user' />
                                    <span className='tw:flex-1'>{t('Personal')}</span>
                                    {organizationIds.length === 0 && <Icon iconName='check' />}
                                </div>
                            </UserMenu.Item>
                            <UserMenu.Item onClick={handleOrganizationScope} disabled={!organizationId}>
                                <div className='tw:flex tw:items-center tw:gap-8'>
                                    <Icon iconName='group' />
                                    <span className='tw:flex-1'>{organizationId || t('Organization')}</span>
                                    {organizationIds.length > 0 && <Icon iconName='check' />}
                                </div>
                            </UserMenu.Item>
                        </>
                    ) : null}

                    {visibleMenu === 'language' ? (
                        <>
                            <BackMenuItem onClick={handleMenuChange('default')} label={t('Back')} />
                            <UserMenu.Divider />
                            {languages.map(({ code, name, flag, isSelected }) => (
                                <UserMenu.Item key={code} onClick={() => changeLanguage(code)}>
                                    <div className='tw:flex tw:items-center tw:gap-8'>
                                        <Flag countryCode={flag ?? 'US'} />
                                        <span className='tw:flex-1'>{name}</span>
                                        {isSelected && <Icon iconName='check' />}
                                    </div>
                                </UserMenu.Item>
                            ))}
                        </>
                    ) : null}

                    {visibleMenu === 'theme' ? (
                        <>
                            <BackMenuItem onClick={handleMenuChange('default')} label={t('Back')} />
                            <UserMenu.Divider />
                            {themeOptions.map(({ label, icon, value }) => (
                                <UserMenu.Item key={label} onClick={() => setTheme(value)}>
                                    <div className='tw:flex tw:items-center tw:gap-8'>
                                        <Icon iconName={icon} />
                                        <span className='tw:flex-1'>{label}</span>
                                        {theme === value && <Icon iconName='check' />}
                                    </div>
                                </UserMenu.Item>
                            ))}
                        </>
                    ) : null}
                </UserMenu.Dropdown>
            </UserMenu>
        </div>
    );
};
